import type { ButtonHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import { InlineSpinner } from "../feedback/InlineSpinner";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
export type ButtonSize = "sm" | "md";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "border-accent bg-accent text-on-accent hover:border-accent-hover hover:bg-accent-hover",
  secondary: "border-ink bg-surface text-ink hover:bg-sunken",
  quiet: "border-transparent bg-transparent text-accent hover:bg-accent-soft",
  danger: "border-danger bg-danger text-on-danger hover:opacity-90",
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-control-sm px-3 text-data",
  md: "h-control px-4 text-ui",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button and shows a spinner in front of the label. */
  busy?: boolean;
}

/**
 * The one button. `primary` is the single blue action per screen, `secondary`
 * the ink-bordered alternative, `quiet` a text action, `danger` a destructive one.
 */
export function Button({
  variant = "primary",
  size = "md",
  busy = false,
  className = "",
  disabled,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const { t } = useTranslation("components");
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      data-component="Button"
      data-variant={variant}
      data-size={size}
      className={cx(
        "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-sm border font-medium disabled:cursor-not-allowed disabled:opacity-60",
        VARIANT[variant],
        SIZE[size],
        className,
      )}
    >
      {busy ? <InlineSpinner decorative size={12} /> : null}
      {busy ? <span className="sr-only">{t("button.busy")}</span> : null}
      {children}
    </button>
  );
}
